import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChildren } from '@angular/core';
import { Hotspot } from '../shared/hotspot.model';
import { LoaderService } from '../../shared/services/loader.service';
import { HotspotService } from '../shared/hotspot.service';
import { HotspotType } from '../shared/hotspot-type.model';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Params } from '@angular/router';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import { ModalService } from '../../shared/services/modal.service';
import { SortDirection } from '../../shared/sort-direction';
import { debounceTime, distinctUntilChanged, finalize, flatMap } from 'rxjs/operators';
import { AutoCompleteAddressService } from '../../shared/services/auto-complete-address.service';
import { AutoCompleteAddress } from '../../shared/auto-complete-address.model';
import { GeocodingService } from '../../shared/services/geocoding.service';
import { Latng } from '../../shared/latng';
import { AuthenticationService } from 'app/shared/services/authentication.service';
import { Region } from 'app/user-dashboard/shared/region.model';
import { RegionService } from 'app/user-dashboard/shared/region.service';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { LOCAL_STORAGE_KEYS } from 'app/core/constants';
import { TranslateService } from '@ngx-translate/core';
import { NavigationLinkService } from '../../shared/services/navigation-link.service';
import LatLngLiteral = google.maps.LatLngLiteral;
import MapMouseEvent = google.maps.MapMouseEvent;

@Component({
  selector: 'app-hotspots-map',
  templateUrl: './hotspots-map.component.html',
  styleUrls: ['./hotspots-map.component.scss']
})
export class HotspotsMapComponent implements OnInit, AfterViewInit {

  infoAlertClosed = false;

  hotspotsTypes: HotspotType[] = [];
  hotspots: Hotspot[]          = [];
  hotspot: Hotspot             = null;
  tempHotspot: Hotspot         = null;

  editModalLabel: string;
  editModalType: HotspotType;

  defaultLat  = 47.988212705125434;
  defaultLng  = 13.40030886232853;
  defaultZoom = 5;

  lat  = this.defaultLat;
  lng  = this.defaultLng;
  zoom = this.defaultZoom;

  latLngLiteralSubject = new Subject<LatLngLiteral>();

  radius = 1000;

  search             = '';
  addresses          = [];
  addressActiveIndex = -1;
  noResult           = false;

  searchKeyUpSubject = new Subject<KeyboardEvent>();

  @ViewChildren('searchElement')
  searchElement;
  regions: Region[] = [];
  selectedRegion: Region;

  constructor(
    private route: ActivatedRoute,
    public location: Location,
    private loader: LoaderService,
    private hotspotService: HotspotService,
    private toastr: ToastrService,
    private geocodingService: GeocodingService,
    private modalService: ModalService,
    private autoCompleteAddressService: AutoCompleteAddressService,
    private cdRef: ChangeDetectorRef,
    private authService: AuthenticationService,
    private regionService: RegionService,
    private errorHandler: ErrorHandlerService,
    private translate: TranslateService,
    public link: NavigationLinkService,
  ) {
    for (const i of Object.keys(HotspotType)) {
      this.hotspotsTypes.push(HotspotType[i]);
    }

    this.searchKeyUpSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.searchAddress();
      });

    route.params.subscribe((params: Params) => {
      const hotspotId = params.id;

      if (!!hotspotId) {
        hotspotService.getById(hotspotId)
          .subscribe(hotspot => {
            this.hotspot = hotspot;

            this.zoomOnHotspot();

            if (this.hotspot.address === null) {
              const latng: Latng = { ...this.hotspot };
              this.geocodingService.reverseGeocode(latng)
                .subscribe(address => {
                  this.hotspot.address = address.formatted;
                });
            }
          });
      }
    });

    this.fetchRegions();
  }

  ngOnInit() {
  }

  ngAfterViewInit() {
    this.searchElement.first.nativeElement.focus();
  }

  searchKeyUp(e: KeyboardEvent, content) {
    if ('Escape' === e.key) {
      this.search = null;
      return this.resetAddresses();
    }
    if ('ArrowUp' === e.key) {
      return this.prevActiveItem();
    }
    if ('ArrowDown' === e.key) {
      return this.nextActiveItem();
    }
    if ('Enter' === e.key && this.addressActiveIndex !== -1) {
      this.selectAddress(this.addresses[this.addressActiveIndex], content);
      return;
    }

    this.searchKeyUpSubject.next(e);
  }

  prevActiveItem() {
    if (this.addresses.length === 0) {
      return;
    }

    if (this.addressActiveIndex === -1) {
      this.addressActiveIndex = 0;
      return;
    }

    if (this.addressActiveIndex > 0) {
      this.addressActiveIndex--;
    }
  }

  nextActiveItem() {
    if (this.addresses.length === 0) {
      return;
    }

    if (this.addressActiveIndex === -1) {
      this.addressActiveIndex = 0;
      return;
    }

    if (this.addressActiveIndex < this.addresses.length - 1) {
      this.addressActiveIndex++;
    }
  }

  moveMarker(hotspot: Hotspot, e: MapMouseEvent): void {
    hotspot.latitude       = e.latLng.lat();
    hotspot.longitude      = e.latLng.lng();
    hotspot.savedLatitude  = e.latLng.lat();
    hotspot.savedLongitude = e.latLng.lng();

    this.loader.startLoad();

    const latng: Latng = { ...hotspot };
    this.geocodingService.reverseGeocode(latng)
      .pipe(
        finalize(() => this.loader.stopLoad()),
        flatMap(address => {
          hotspot.address     = address.formatted;
          hotspot.country     = address.country;
          hotspot.countryCode = address.countryCode;

          return this.hotspotService.update(hotspot);
        })
      )
      .subscribe(h => {
        this.hotspot = h;
        this.search  = this.hotspot.address;
        this.toastr.success('Hotstop position successfully updated', 'Success');
      }, error => {
        console.log(error);
        this.toastr.error('Error while updating the hotspot position', 'Error');
      });
  }

  movetempMarker(e: MapMouseEvent): void {
    this.loader.startLoad();

    const latng: Latng = { latitude: e.latLng.lat(), longitude: e.latLng.lng() };
    this.geocodingService.reverseGeocode(latng)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(a => {
        this.search = a.formatted;

        this.tempHotspot.latitude    = a.latitude;
        this.tempHotspot.longitude   = a.longitude;
        this.tempHotspot.address     = a.formatted;
        this.tempHotspot.country     = a.country;
        this.tempHotspot.countryCode = a.countryCode;
      });
  }

  dblClickMap(e: MapMouseEvent): void {
    this.hotspot = null;

    this.loader.startLoad();

    const latng: Latng = { latitude: e.latLng.lat(), longitude: e.latLng.lng() };
    this.geocodingService.reverseGeocode(latng)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(a => {
        this.search = a.formatted;

        this.lat  = a.latitude;
        this.lng  = a.longitude;
        this.zoom = 15;

        this.tempHotspot             = new Hotspot();
        this.tempHotspot.latitude    = a.latitude;
        this.tempHotspot.longitude   = a.longitude;
        this.tempHotspot.address     = a.formatted;
        this.tempHotspot.country     = a.country;
        this.tempHotspot.countryCode = a.countryCode;
      });
  }

  openConfirmMoveHotspot(hotspot: Hotspot, e: MapMouseEvent, content): void {
    this.modalService.open({
      title: 'Move Hotspot',
      content,
      closeButton: {
        text: 'No'
      },
      validateButton: {
        text: 'Yes',
        icon: 'fa fa-save'
      }
    }).subscribe(
      () => this.moveMarker(hotspot, e),
      () => {
        const hotspotBackup     = Object.assign({}, hotspot);
        hotspotBackup.latitude  = hotspotBackup.savedLatitude;
        hotspotBackup.longitude = hotspotBackup.savedLongitude;

        const index = this.hotspots.findIndex(h => h.id === hotspot.id);
        this.hotspots.splice(index, 1);

        this.hotspots.push(hotspotBackup);
        this.hotspot = hotspotBackup;

        this.search = hotspotBackup.address;
      }
    );

  }

  openEditHotspot(content): void {
    this.editModalLabel = this.hotspot.label;
    this.editModalType  = this.hotspot.type;

    this.modalService.open({
      title: `Hotspot ${!this.hotspot.id ? 'creation' : 'edition'}`,
      content,
      validateButton: {
        text: `${!this.hotspot.id ? 'Create' : 'Update'}`,
        icon: 'fa fa-save',
        disabled: () => !this.editModalLabel || !this.editModalType
      },
      size: 'sm'
    }).subscribe(() => {

      this.hotspot.label = this.editModalLabel;
      this.hotspot.type  = this.editModalType;

      if (!this.hotspot.id) {
        this.createHotspot(this.hotspot);
      } else {
        this.loader.startLoad();

        this.hotspotService.update(this.hotspot)
          .pipe(finalize(() => this.loader.stopLoad()))
          .subscribe(() => {
            this.resetEditModal();
            this.toastr.success('Hotstop successfully updated', 'Success');
          }, error => {
            console.log(error);
            this.toastr.error('Error while updating the hotspot', 'Error');
          });
      }
    }, () => {
      this.resetEditModal();

      if (!this.hotspot.id) {
        this.hotspot = null;
      }
    });
  }

  createHotspot(hotspot: Hotspot): void {
    this.loader.startLoad();

    const latng: Latng = { ...hotspot };
    this.geocodingService.reverseGeocode(latng)
      .pipe(
        finalize(() => this.loader.stopLoad()),
        flatMap(address => {
          hotspot.address     = address.formatted;
          hotspot.country     = address.country;
          hotspot.countryCode = address.countryCode;

          return this.hotspotService.create(hotspot);
        })
      )
      .subscribe(h => {
        this.resetEditModal();

        this.tempHotspot = null;
        this.hotspot     = h;
        this.hotspots.push(this.hotspot);
        this.toastr.success('Hotstop successfully created', 'Success');
      }, error => {
        console.log(error);
        this.toastr.error('Error while creating the hotspot', 'Error');
      });
  }

  searchAddress(): void {
    if (this.search === '' || this.search === null) {
      this.resetAddresses();
      return;
    }

    this.autoCompleteAddressService.search(this.search).subscribe(addresses => {
      if (addresses && addresses.length > 0) {
        this.addresses          = addresses;
        this.addressActiveIndex = 0;
        this.noResult           = false;
      } else {
        this.addresses          = [];
        this.addressActiveIndex = -1;
        this.noResult           = true;
      }

      this.cdRef.detectChanges();
    });
  }

  selectAddress(address: AutoCompleteAddress, content): void {
    this.resetAddresses();

    if (this.hotspot === null) {
      this.search = address.description;

      this.lat  = address.latitude;
      this.lng  = address.longitude;
      this.zoom = 15;

      this.tempHotspot             = new Hotspot();
      this.tempHotspot.latitude    = address.latitude;
      this.tempHotspot.longitude   = address.longitude;
      this.tempHotspot.address     = address.description;
      this.tempHotspot.country     = address.country;
      this.tempHotspot.countryCode = address.countryCode;
      return;
    }

    this.hotspot.latitude  = address.latitude;
    this.hotspot.longitude = address.longitude;

    this.lat = address.latitude;
    this.lng = address.longitude;

    this.openConfirmMoveHotspot(this.hotspot, { latLng: new google.maps.LatLng(address.latitude, address.longitude), domEvent: null, stop: null }, content);
  }

  unselectAddress(): void {
    this.search      = null;
    this.hotspot     = null;
    this.tempHotspot = null;

    this.resetEditModal();
  }

  selectHotspot(h: Hotspot): void {
    this.search      = h.address;
    this.hotspot     = h;
    this.tempHotspot = null;
  }

  selectTempHotspot(content): void {
    this.hotspot = this.tempHotspot;
    this.openEditHotspot(content);
  }

  zoomOnHotspot(): void {
    this.zoom = 15;

    this.lat = this.hotspot.latitude;
    this.lng = this.hotspot.longitude;
  }

  resetZoom(): void {
    this.zoom        = this.defaultZoom;
    this.lat         = this.defaultLat;
    this.lng         = this.defaultLng;
    this.tempHotspot = null;
  }

  resetAddresses(): void {
    this.addresses          = [];
    this.addressActiveIndex = -1;
    this.noResult           = false;
  }

  resetEditModal(): void {
    this.editModalLabel = null;
    this.editModalType  = null;
  }

  centerChange(e): void {
    this.latLngLiteralSubject.next(e);
  }

  changeRadius(): void {
    this.loader.startLoad();

    this.hotspotService.update(this.hotspot)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success('Hotspot radius successfully updated', 'Success');
      }, error => {
        console.log(error);
        this.toastr.error('Error while updating the hotspot radius', 'Error');
      });
  }

  openConfirmDelete(content): void {
    this.modalService.open({
      title: 'Delete Hotspot',
      content,
      closeButton: {
        text: 'No, Keep it'
      },
      validateButton: {
        type: 'danger',
        text: 'Yes, Delete it',
        icon: 'fa fa-trash'
      }
    }).subscribe(() => this.removeHotspot());
  }

  removeHotspot(): void {
    this.loader.startLoad();

    this.hotspotService.remove(this.hotspot.id)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {

        const index = this.hotspots.findIndex(h => h.id === this.hotspot.id);
        this.hotspots.splice(index, 1);

        this.search = null;
        this.resetAddresses();
        this.hotspot = null;
        this.toastr.success('Hotstop successfully deleted', 'Success');
      }, error => {
        console.error(error);
        this.toastr.error('Error while deleting the hotspot', 'Error');
      });
  }

  fetchRegions() {
    this.loader.startLoad();
    this.regionService.getAllLight()
      .pipe(
        finalize(() => this.loader.stopLoad()),
      )
      .subscribe(regions => {
        this.regions = regions;
        if (!this.selectedRegion && !this.hotspot && localStorage.getItem(LOCAL_STORAGE_KEYS.HOTSPOT_MAP_REGION)) {
          this.selectedRegion = regions.find(region =>
            region.id === localStorage.getItem(LOCAL_STORAGE_KEYS.HOTSPOT_MAP_REGION)
          );
          if (this.selectedRegion) {
            this.loadSelectedRegionHotspots();
          }
        }
      }, e => this.errorHandler.handleHttpError(e, 'forced-checkin.errors.fetch-regions'));
  }

  saveSelectedRegion() {
    if (this.selectedRegion) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.HOTSPOT_MAP_REGION, this.selectedRegion.id);
      this.loadSelectedRegionHotspots();
    }
  }

  loadSelectedRegionHotspots() {
    this.route.params.subscribe((params: Params) => {
      const hotspotId = params.id;

      this.loader.startLoad(false);

      this.hotspotService.search(0, 20000, 'country_code', SortDirection.ASC, {regionId: this.selectedRegion.id})
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(result => {
          this.hotspots = result.results;

          if (!!hotspotId) {
            const index = result.results.findIndex(h => h.id === hotspotId);

            if (index !== -1) {
              this.hotspot = result.results[index];

              this.zoomOnHotspot();

              if (this.hotspot.address === null) {
                const latng: Latng = { ...this.hotspot };
                this.geocodingService.reverseGeocode(latng)
                  .subscribe(address => {
                    this.hotspot.address = address.formatted;
                  });
              }
            }
          }

        }, error => {
          this.hotspots = [];
          console.log(error);
          this.toastr.error(error.error && error.error.message || this.translate.instant('hotspots.errors.hotspots-fetching'));
        });
    });
  }
}
